/* BergHOFF V5 — collection.js
 * Enhances the server-rendered collection/search listing:
 * - filters + sorting update the grid via the Section Rendering API (no full reload)
 * - mobile filter sheet (works without JS through :target)
 * - "Load more" appends the next page; numbered pagination links remain in the HTML
 */
(() => {
  'use strict';

  const qs = (sel, root = document) => root.querySelector(sel);
  const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const getRoot = () => qs('[data-collection]');
  if (!getRoot()) return;

  const announce = (msg) => window.BergHOFF?.announce?.(msg);
  const lock = (state) => window.BergHOFF?.lockScroll?.('facets', state);
  let controller;
  let priceTimer;

  const cleanUrl = (url) => {
    const u = new URL(url, window.location.origin);
    u.searchParams.delete('section_id');
    return u.pathname + (u.search ? u.search : '');
  };

  const formUrl = (form) => {
    const params = new URLSearchParams();
    new FormData(form).forEach((value, key) => {
      if (String(value).trim() !== '') params.append(key, value);
    });
    const base = new URL(form.getAttribute('action') || window.location.pathname, window.location.origin).pathname;
    const query = params.toString();
    return query ? `${base}?${query}` : base;
  };

  /* ---------- Mobile filter sheet ---------- */
  const facets = () => qs('[data-facets]', getRoot());
  const openSheet = () => {
    const panel = facets();
    if (!panel) return;
    panel.classList.add('is-open');
    lock(true);
    qs('[data-facets-open]')?.setAttribute('aria-expanded', 'true');
    // Focus once the sheet is visible (visibility switches on the next style pass).
    window.setTimeout(() => qs('[data-facets-close]', panel)?.focus({ preventScroll: true }), 30);
  };
  const closeSheet = (returnFocus = true) => {
    const panel = facets();
    if (!panel || !panel.classList.contains('is-open')) return;
    panel.classList.remove('is-open');
    lock(false);
    const opener = qs('[data-facets-open]');
    opener?.setAttribute('aria-expanded', 'false');
    if (returnFocus) opener?.focus();
  };

  /* ---------- Render ---------- */
  const swap = (fresh) => {
    const current = getRoot();
    const openFacets = new Set(qsa('details[data-facet][open]', current).map((d) => d.dataset.facet));
    const knownFacets = new Set(qsa('details[data-facet]', current).map((d) => d.dataset.facet));
    const sheetOpen = facets()?.classList.contains('is-open');
    const activeId = document.activeElement?.id;
    const bodyScroll = qs('.facets__body', current)?.scrollTop || 0;

    current.innerHTML = fresh.innerHTML;

    qsa('details[data-facet]', current).forEach((d) => {
      if (knownFacets.has(d.dataset.facet)) d.open = openFacets.has(d.dataset.facet);
    });
    if (sheetOpen) {
      facets()?.classList.add('is-open');
      qs('[data-facets-open]')?.setAttribute('aria-expanded', 'true');
    }
    const body = qs('.facets__body', current);
    if (body) body.scrollTop = bodyScroll;
    if (activeId) document.getElementById(activeId)?.focus({ preventScroll: true });

    const count = qs('[data-product-count]', current);
    if (count) announce(count.textContent.trim());
  };

  const render = async (url, { push = true } = {}) => {
    const root = getRoot();
    const sectionId = root.dataset.sectionId;
    controller?.abort();
    controller = new AbortController();
    const target = new URL(url, window.location.origin);
    target.searchParams.set('section_id', sectionId);
    qs('[data-grid-container]', root)?.classList.add('is-loading');

    try {
      const res = await fetch(target, { signal: controller.signal });
      if (!res.ok) throw new Error(String(res.status));
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const fresh = qs('[data-collection]', doc);
      if (!fresh) throw new Error('missing section');
      swap(fresh);
      if (push) window.history.pushState({ collection: true }, '', cleanUrl(url));
    } catch (err) {
      if (err.name !== 'AbortError') window.location.assign(cleanUrl(url));
    }
  };

  /* ---------- Events (delegated; survive re-renders) ---------- */
  document.addEventListener('change', (event) => {
    const form = event.target.closest('[data-facets-form]');
    if (form && getRoot()?.contains(form)) {
      if (event.target.matches('[data-price-input]')) {
        window.clearTimeout(priceTimer);
        priceTimer = window.setTimeout(() => render(formUrl(form)), 600);
      } else {
        render(formUrl(form));
      }
      return;
    }
    if (event.target.matches('[data-sort-select]')) {
      const sortForm = event.target.closest('[data-sort-form]');
      if (sortForm) render(formUrl(sortForm));
    }
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (form.matches('[data-facets-form]')) {
      event.preventDefault();
      window.clearTimeout(priceTimer);
      render(formUrl(form));
      closeSheet();
    } else if (form.matches('[data-sort-form]')) {
      event.preventDefault();
      render(formUrl(form));
    }
  });

  document.addEventListener('click', (event) => {
    const facetLink = event.target.closest('[data-facet-link]');
    if (facetLink && getRoot()?.contains(facetLink)) {
      event.preventDefault();
      render(facetLink.getAttribute('href'));
      return;
    }
    if (event.target.closest('[data-facets-open]')) {
      event.preventDefault();
      openSheet();
      return;
    }
    if (event.target.closest('[data-facets-close]')) {
      event.preventDefault();
      closeSheet();
      return;
    }
    const more = event.target.closest('[data-load-more]');
    if (more) loadMore(more);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeSheet();
  });

  window.addEventListener('popstate', () => render(window.location.href, { push: false }));

  // Visiting /collections/x#Facets-… without JS opens the sheet via :target; with JS use the class.
  if (window.location.hash.startsWith('#Facets-')) {
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
  }

  /* ---------- Load more ---------- */
  const loadMore = async (button) => {
    const root = getRoot();
    const grid = qs('[data-product-grid]', root);
    const nextUrl = button.dataset.nextUrl;
    if (!grid || !nextUrl) return;
    button.setAttribute('aria-disabled', 'true');
    button.classList.add('is-loading');
    try {
      const target = new URL(nextUrl, window.location.origin);
      target.searchParams.set('section_id', root.dataset.sectionId);
      const res = await fetch(target);
      if (!res.ok) throw new Error(String(res.status));
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const items = qsa('[data-grid-item]', doc);
      const firstNew = items[0];
      items.forEach((item) => grid.appendChild(document.importNode(item, true)));
      const freshPagination = qs('[data-pagination]', doc);
      const currentPagination = qs('[data-pagination]', root);
      if (currentPagination) {
        if (freshPagination) currentPagination.replaceWith(freshPagination);
        else currentPagination.remove();
      }
      if (firstNew) {
        const link = grid.querySelectorAll('[data-grid-item]')[grid.querySelectorAll('[data-grid-item]').length - items.length]?.querySelector('.card__link');
        link?.focus({ preventScroll: true });
      }
      const status = qs('[data-load-more-status]', root);
      announce(status ? status.textContent.trim() : '');
    } catch (err) {
      window.location.assign(nextUrl);
    } finally {
      button.removeAttribute('aria-disabled');
      button.classList.remove('is-loading');
    }
  };
})();
